import React, { useState, useEffect } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useNavigate } from "react-router-dom";
import { getter } from "@progress/kendo-react-common";
import { useDispatch } from "react-redux";
import taskImg from "../../../Images/tasks.png";
import { loadClientTaskGridData } from "../../../handlers/ClientDetail/ClientTaskHandler";

import useReloadButton from "../../../CustomHooks/useReloadButton";

import { useKendoGridHandlers } from "../../../CustomHooks/useKendoGridHandlers";
import { setSearchFrom } from "../../../store/reducers/SharedLayoutDataSlice";

const DATA_ITEM_KEY = "SEQ_NUM";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_CLIENT_TASK = {
	action: "LOADCLIENTTASKS",
	className: "TASK",
	pageSize: 20,
	currentPageNumber: 1,
	sortName: "A_SHORT_NAME",
	sortOrder: "desc",
	gridHeight: "300px",
};

const PAGER_SETTINGS = {
	info: true,
	type: "input",
	previousNext: true,
	pageSize: 4,
	refresh: true,
};

const KendoClientDetailTasks = React.memo((props) => {

	const dispatch = useDispatch();

	const { data } = props;
	const [gridData, setGridData] = useState([]);
	const [total, setTotal] = useState(0);
	const navigate = useNavigate();
	const [reloadToggle, setReloadToggle] = useState(false);


	const [gridState, setGridState] = useState({
		pageNumber: DEFAULT_GRID_INFO_CLIENT_TASK.currentPageNumber,
		pageSize: DEFAULT_GRID_INFO_CLIENT_TASK.pageSize,
		sortName: DEFAULT_GRID_INFO_CLIENT_TASK.sortName,
		sortOrder: DEFAULT_GRID_INFO_CLIENT_TASK.sortOrder,
		sort: [
			{
				field: DEFAULT_GRID_INFO_CLIENT_TASK.sortName,
				dir: DEFAULT_GRID_INFO_CLIENT_TASK.sortOrder?.toLowerCase() || "asc",
			},
		],
	});

	const { HandleDataStateChange, HandlePageChange } =
		useKendoGridHandlers(setGridState);

	const [selectedState, setSelectedState] = React.useState();
	useEffect(() => {
		loadClientTaskGridData(data, gridState).then((response) => {
			setGridData(response.rows);
			setTotal(response.total);
		});
	}, [props, gridState, reloadToggle]);

	useReloadButton(setReloadToggle);

	const SelectedContentLength = () => {
		const selectedText = window.getSelection().toString();
		return selectedText.length > 0;
	};

	const onSelectionChange = React.useCallback(
		(event) => {
			const check = SelectedContentLength();
			if (!check) {
				const newSelectedState = getSelectedState({
					event,
					selectedState,
					dataItemKey: DATA_ITEM_KEY,
				});
				setSelectedState(newSelectedState);
				const currentColumn = event.target._columns[event.endColIndex].field;
				if (currentColumn === "SEQ_NUM") {
					ShowTaskDetail(event.dataItems[event.endRowIndex][currentColumn]);
				}
			}
		},
		[selectedState]
	);



	const CellStatus = (props) => {
		if (props.dataItem.STATUS === "J") {
			return (
				<td>
					<span className="ticket-open w-75">Pending/Over Due</span>
				</td>
			);
		} if (props.dataItem.STATUS === "O" || props.dataItem.STATUS === "I") {
			return (
				<td>
					<span className="ticket-open w-75">Pending</span>
				</td>
			);
		} if (props.dataItem.STATUS === "R" || props.dataItem.STATUS === "r") {
			return (
				<td>
					<span className="ticket-open w-75">Completed</span>
				</td>
			);
		} if (props.dataItem.STATUS === "Q" || props.dataItem.STATUS === "q") {
			return (
				<td>
					<span className="ticket-open w-75">Pending On Client</span>
				</td>
			);
		}
	};

	const TaskDetail = (props) => (
			<td>
				<span className="mr-2">
					<img src={taskImg} width={10} alt="" />
				</span>

				<span
					role="button"
					className="taskNo"
					onClick={() => ShowTaskDetail(props.dataItem.TICKET_NO, props.dataItem.TICKET_TYPE)}
				>
					#{props.dataItem.TICKET_NO}
				</span>
			</td>
		);

	const ShowTaskDetail = (taskId, ticketType) => {
		dispatch(setSearchFrom("clientDetail"));
		navigate("/ticketDetail", {
			state: {
				ticketNo: taskId,
				task: ticketType === "Training And Implementation",
			},
		});

	};

	const MakeDateField = (props) => {

		const DateString = props.dataItem[props.field];
		let formattedDate = "";
		if (DateString) {
			const parsedDate = new Date(DateString);
			if (!isNaN(parsedDate)) {
				formattedDate = parsedDate.toLocaleDateString("en-US"); // e.g. MM/DD/YYYY
			}

		}
		return (
			<td className='pl-4' >
				{formattedDate}
			</td>
		);
	}


	const CellComments = (props) => (
			<td className="Comments30 pl-4">
				<span className="textWrap" title={props.dataItem[props.field]}>
					{props.dataItem[props.field]}
				</span>
			</td>
		);

	return (
		<div>
			<Tooltip openDelay={100} position="right" anchorElement="target">
				<Grid
					style={{ height: "calc(100vh - 217px)" }}
					scrollable="scrollable"
					total={total}
					skip={(gridState.pageNumber - 1) * gridState.pageSize}
					pageSize={gridState.pageSize}
					filterable={false}
					selectedField={SELECTED_FIELD}
					sortable={{
						mode: "single",
						allowUnsort: false,
					}}
					sort={gridState.sort}
					data={gridData.map((item) => ({
						...item,
						[SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
					}))}
					selectable={{
						enabled: true,
						drag: false,
						cell: false,
						mode: "multiple",
					}}
					pageable={PAGER_SETTINGS}
					onPageChange={HandlePageChange}
					onDataStateChange={HandleDataStateChange}
					onSelectionChange={onSelectionChange}
				>
					<GridColumn title="Status" field="STATUS" cell={CellStatus} />
					<GridColumn title="Task #" field="SEQ_NUM" cell={TaskDetail} />
					<GridColumn
						title="Description"
						field="COMMENTS"
						sortable
						cell={CellComments}
						filterable={false}
					/>
					<GridColumn title="Assignee" field="A_SHORT_NAME" />
					<GridColumn title="Reporter" field="REPORTER_SHORT_NAME" />
					<GridColumn title="Priority" field="PRIORITY" className="pl-4" />
					<GridColumn
						title="Created Date"
						field="CREATED_DATE"
						className="ml-3"
						cell={MakeDateField}
					/>
					<GridColumn
						title="Last Modified"
						field="CREATED_DATE"
						cell={MakeDateField}
					/>
				</Grid>
			</Tooltip>
		</div >
	);
});
export default KendoClientDetailTasks;
